import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { MessageSquare, Share2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * ShareAppButton — invites a non-family friend to try the Kinsy beta.
 *
 * Behaviour, in order of preference:
 *   1. `navigator.share()` — native OS share sheet (iOS/Android): Messages,
 *      Mail, WhatsApp, AirDrop, Copy… User picks Text and iMessage/SMS opens
 *      pre-filled. Best UX on the platforms we care about most.
 *   2. `sms:` URI — desktop or a browser without Web Share falls back to
 *      opening the system Messages app with a pre-filled body.
 *   3. Clipboard copy — belt-and-braces last resort so the user still walks
 *      away with the link even if nothing else opens.
 *
 * The download URL lives in REACT_APP_BETA_DOWNLOAD_URL so it can be swapped
 * to the real TestFlight join link without a code push.
 */

const BETA_URL = process.env.REACT_APP_BETA_DOWNLOAD_URL || 'https://kinsy.io/beta';
const DEFAULT_MESSAGE = `I've been using Kinsy — a private social network just for family. Try the beta: ${BETA_URL}`;

export default function ShareAppButton({
  variant = 'default',
  size = 'default',
  className = '',
  label = 'Share Kinsy via text',
  message = DEFAULT_MESSAGE,
  testId = 'share-app-button',
}) {
  const [busy, setBusy] = useState(false);

  const handleShare = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // 1. Native share sheet — iOS/Android modern browsers + Capacitor.
      if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
        try {
          await navigator.share({
            title: 'Kinsy — private family network',
            text: message,
            url: BETA_URL,
          });
          return;
        } catch (err) {
          // User dismissed the share sheet — that's a normal outcome, no toast.
          if (err && err.name === 'AbortError') return;
          // Any other error: fall through to sms: fallback.
        }
      }

      // 2. sms: URI fallback. iOS uses `&body=`, Android uses `?body=`.
      // The `?body=` form works on both — iOS 8+ accepts either.
      const smsHref = `sms:?body=${encodeURIComponent(message)}`;
      // Anchor click keeps this a user-initiated navigation (required on iOS).
      const a = document.createElement('a');
      a.href = smsHref;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // 3. Also copy to clipboard so it's never lost.
      if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
          await navigator.clipboard.writeText(message);
          toast.success('Link copied — paste it into any text app.');
        } catch (_) { /* ignore clipboard failures */ }
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      type="button"
      onClick={handleShare}
      variant={variant}
      size={size}
      className={className}
      disabled={busy}
      data-testid={testId}
      aria-label={label}
    >
      <MessageSquare className="w-4 h-4 mr-2" />
      {label}
      <Share2 className="w-3.5 h-3.5 ml-2 opacity-70" />
    </Button>
  );
}
